import React from 'react';
import KineticPresetPreview from './clip-editor/KineticPresetPreview';

/** Sent as `settings.kineticCaption` on video / video_link jobs. Omit the key entirely for "off". */
export interface KineticCaptionSettings {
  preset: string;
  fontFamily: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  textColor: string;
  highlightColor: string;
  emphasis: boolean;
  fontSizePct: number;
  marginVPct: number;
}

// Same ids/labels as the caption editor's presets (pages/KineticCaption.tsx).
const PRESETS: { id: string; label: string }[] = [
  { id: 'word-pop', label: 'Word Pop' },
  { id: 'karaoke-box', label: 'Karaoke Box' },
  { id: 'karaoke-color', label: 'Karaoke Color' },
  { id: 'karaoke-scale', label: 'Karaoke Scale' },
  { id: 'fill-sweep', label: 'Fill Sweep' },
  { id: 'bounce-in', label: 'Bounce In' },
  { id: 'typewriter', label: 'Typewriter' },
  { id: 'underline-marker', label: 'Underline Marker' },
  { id: 'build-up', label: 'Build Up' },
];

const CAPTION_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
  'Oswald', 'Bebas Neue', 'Anton', 'Arial', 'Georgia', 'Impact',
];

const FONT_PCT_MIN = 2;
const FONT_PCT_MAX = 15;
const FONT_PCT_STEP = 0.5;
const MARGIN_PCT_MIN = 0;
const MARGIN_PCT_MAX = 50;

export const DEFAULT_KINETIC_CAPTION: KineticCaptionSettings = {
  preset: 'word-pop',
  fontFamily: 'Arial',
  bold: true,
  italic: false,
  underline: false,
  textColor: '#FFFFFF',
  highlightColor: '#FFD700',
  emphasis: true,
  fontSizePct: 6,
  marginVPct: 27,
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

interface Props {
  value: KineticCaptionSettings | null;
  onChange: (value: KineticCaptionSettings | null) => void;
}

const KineticCaptionPicker: React.FC<Props> = ({ value, onChange }) => {
  const set = (patch: Partial<KineticCaptionSettings>) => {
    if (value) onChange({ ...value, ...patch });
  };

  return (
    <div className="cm-kc">
      <label className={`opt-row ${value ? 'opt-row--active' : ''}`}>
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={e => onChange(e.target.checked ? { ...DEFAULT_KINETIC_CAPTION } : null)}
        />
        <span className="opt-row-body">
          <span className="opt-row-title">Add kinetic captions</span>
          <span className="cm-field-help">Burns word-by-word animated captions into the shorts. Off by default.</span>
        </span>
      </label>

      {value && (
        <>
          <div className="kc-preset-grid cm-kc-presets">
            {PRESETS.map(p => (
              <button
                key={p.id}
                type="button"
                className={`kc-preset-card ${value.preset === p.id ? 'kc-preset-card--active' : ''}`}
                onClick={() => set({ preset: p.id })}
              >
                <span className="kc-preset-thumb"><KineticPresetPreview presetId={p.id} /></span>
                <span className="kc-preset-label">{p.label}</span>
              </button>
            ))}
          </div>

          <div className="cm-kc-grid">
            <div className="modal-field">
              <label>Font</label>
              <select value={value.fontFamily} onChange={e => set({ fontFamily: e.target.value })}>
                {CAPTION_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="modal-field">
              <label>Size (% of height)</label>
              <input
                type="number"
                min={FONT_PCT_MIN}
                max={FONT_PCT_MAX}
                step={FONT_PCT_STEP}
                value={value.fontSizePct}
                onChange={e => {
                  const v = Number(e.target.value);
                  if (!isNaN(v)) set({ fontSizePct: clamp(v, FONT_PCT_MIN, FONT_PCT_MAX) });
                }}
              />
            </div>
            <div className="modal-field">
              <label>Bottom margin (%)</label>
              <input
                type="number"
                min={MARGIN_PCT_MIN}
                max={MARGIN_PCT_MAX}
                step={1}
                value={value.marginVPct}
                onChange={e => {
                  const v = Number(e.target.value);
                  if (!isNaN(v)) set({ marginVPct: clamp(v, MARGIN_PCT_MIN, MARGIN_PCT_MAX) });
                }}
              />
            </div>
            <div className="modal-field">
              <label>Style</label>
              <div className="cm-kc-style-row">
                <button type="button" className={`cm-kc-fmt ${value.bold ? 'cm-kc-fmt--on' : ''}`} aria-pressed={value.bold} title="Bold" onClick={() => set({ bold: !value.bold })}><b>B</b></button>
                <button type="button" className={`cm-kc-fmt ${value.italic ? 'cm-kc-fmt--on' : ''}`} aria-pressed={value.italic} title="Italic" onClick={() => set({ italic: !value.italic })}><i>I</i></button>
                <button type="button" className={`cm-kc-fmt ${value.underline ? 'cm-kc-fmt--on' : ''}`} aria-pressed={value.underline} title="Underline" onClick={() => set({ underline: !value.underline })}><u>U</u></button>
                <label className="cm-kc-color" title="Text color">
                  <span className="cm-kc-color-chip" style={{ background: value.textColor }} />
                  <input type="color" value={value.textColor} onChange={e => set({ textColor: e.target.value.toUpperCase() })} />
                </label>
                <label className="cm-kc-color" title="Highlight color">
                  <span className="cm-kc-color-chip cm-kc-color-chip--hl" style={{ background: value.highlightColor }} />
                  <input type="color" value={value.highlightColor} onChange={e => set({ highlightColor: e.target.value.toUpperCase() })} />
                </label>
              </div>
            </div>
          </div>

          <label className="cm-kc-check">
            <input type="checkbox" checked={value.emphasis} onChange={e => set({ emphasis: e.target.checked })} />
            <span>Emphasis on key words</span>
          </label>
        </>
      )}
    </div>
  );
};

export default KineticCaptionPicker;
